import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { searchPlaces, reverseGeocode, type PlaceSuggestion } from "@/lib/distance.functions";
import { MapPin, Loader2, LocateFixed } from "lucide-react";

interface Props {
  label: string;
  placeholder?: string;
  value: PlaceSuggestion | null;
  onChange: (v: PlaceSuggestion | null) => void;
  allowCurrentLocation?: boolean;
}

export function AddressInput({ label, placeholder, value, onChange, allowCurrentLocation }: Props) {
  const search = useServerFn(searchPlaces);
  const reverse = useServerFn(reverseGeocode);
  const [query, setQuery] = useState(value?.label ?? "");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const useMyLocation = () => {
    setLocateError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocateError("Geolocation not supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await reverse({ data: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          if (r) {
            onChange(r);
            setQuery(r.label);
            setOpen(false);
          }
        } catch {
          setLocateError("Could not resolve your address.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        setLocateError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Enable it in your browser to use this."
            : "Could not get your location. Please type your address.",
        );
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  useEffect(() => {
    if (value && value.label !== query) setQuery(value.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!query || query.length < 1 || query === value?.label) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await search({ data: { q: query } });
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [query, value?.label, search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </label>
        {allowCurrentLocation && (
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-60"
          >
            {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : <LocateFixed className="h-3 w-3" />}
            {locating ? "Locating…" : "Use my location"}
          </button>
        )}
      </div>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          placeholder={placeholder ?? "Address, hotel, airport..."}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          className="w-full rounded-md border border-border bg-input pl-9 pr-9 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {open && query.length >= 1 && query !== value?.label && (suggestions.length > 0 || (!loading && suggestions.length === 0)) && (
        <ul className="absolute z-30 mt-2 w-full max-h-72 overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {suggestions.length === 0 && !loading ? (
            <li className="px-4 py-2.5 text-sm text-muted-foreground">No matches in Morocco. Try a different spelling.</li>
          ) : (
            suggestions.map((s, i) => (
              <li key={i}>
                <button
                  type="button"
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted"
                  onClick={() => {
                    onChange(s);
                    setQuery(s.label);
                    setOpen(false);
                    setSuggestions([]);
                  }}
                >
                  {s.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {locateError && <p className="mt-1.5 text-[11px] text-destructive">{locateError}</p>}
    </div>
  );
}
